import {
	type BatchTransactionProps,
	type FilterParams,
	useBatchTransactions,
} from '@api/hooks/useBatch.ts';
import { NoFilteredResults } from '@components/EmptyState/NoFilteredResults.tsx';
import { useBankProfile } from '@contexts/BankProfileContext.tsx';
import {
	type ColumnProps,
	DEFAULT_PAGE_SIZE,
	DropdownPositions,
	Pagination,
	Table,
} from '@gurupay/gp-ui-library';
import { useClearFilter } from '@hooks/useClearFilter.ts';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BatchStatus, OperationStatus } from 'types/operations.ts';
import { Capability } from 'types/roles.ts';

import { BatchTransactionFilters } from './BatchTransactionsFilter.tsx';
import { transactionRenderers } from './transactionRenderers.tsx';

const DEFAULT_TRANSACTION_STATUSES = [
	OperationStatus.CREATED,
	OperationStatus.SIGNED,
	OperationStatus.PROCESSED,
	OperationStatus.FAILED,
	OperationStatus.EXECUTED,
];

const getDefaultFilters = (): FilterParams => ({
	status: DEFAULT_TRANSACTION_STATUSES,
});

interface TransactionTableProps {
	batchId: string;
	batchStatus: BatchStatus;
	isDeleteDisabled: boolean;
}

export const TransactionTable = ({
	batchId,
	batchStatus,
	isDeleteDisabled,
}: TransactionTableProps) => {
	const { t } = useTranslation('', { keyPrefix: 'payments.batch.table' });
	const { hasCapability } = useBankProfile();
	const navigate = useNavigate();
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [filters, setFilters] = useState(getDefaultFilters());

	const { data, isLoading } = useBatchTransactions({
		batchId,
		limit: pageSize,
		currentPage,
		filters,
	});

	const filterForm = useForm<FilterParams>({
		defaultValues: getDefaultFilters(),
	});

	const { hasFilters, handleClear } = useClearFilter(filterForm, onFiltersSubmit);

	const shouldGoToPreviousPage =
		!isLoading &&
		data &&
		currentPage > 1 &&
		data.transactions.items.length === 0 &&
		data.transactions.pagination.count > 0;

	useEffect(() => {
		if (shouldGoToPreviousPage) {
			setCurrentPage((prev) => prev - 1);
		}
	}, [shouldGoToPreviousPage]);

	function onFiltersSubmit(filterOptions: FilterParams) {
		setFilters(filterOptions);
		setCurrentPage(1);
	}

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setCurrentPage(1);
	};

	const PAGE_SIZE_OPTIONS = useMemo(
		() => [
			{ label: t('options.show25'), value: 25 },
			{ label: t('options.show50'), value: 50 },
			{ label: t('options.show75'), value: 75 },
		],
		[t],
	);

	const canDeletePayment = hasCapability(Capability.DELETE_PAYMENT);

	const columns: ColumnProps[] = [
		{ label: t('beneficiary'), key: 'beneficiary', align: 'left' },
		{ label: t('vop'), key: 'vop', align: 'left' },
		{ label: t('description'), key: 'description', align: 'left' },
		{ label: t('status'), key: 'status', align: 'left' },
		{ label: t('operationsCount'), key: 'operation', align: 'left' },
		{ label: t('totalAmount'), key: 'amount', align: 'right' },
		...(canDeletePayment ? [{ label: '', key: 'delete' }] : []),
	];

	const handleRowClick = (transaction: BatchTransactionProps) => {
		navigate(`/payments/${transaction.id}`);
	};

	const isTableLoading = isLoading || batchStatus === BatchStatus.INITIALIZING;
	const items = data?.transactions;

	return (
		<FormProvider {...filterForm}>
			{!isTableLoading && !!items?.items?.length && (
				<BatchTransactionFilters
					handleClear={handleClear}
					hasFilters={hasFilters}
					onFiltersSubmit={onFiltersSubmit}
				/>
			)}

			{!isTableLoading && !items?.items?.length && hasFilters ? (
				<NoFilteredResults onClear={handleClear} />
			) : (
				<Table
					className="border-t border-t-neutral-100 mt-space-8"
					columns={columns}
					emptyTablePlaceholder={t('empty')}
					isLoading={isTableLoading}
					onRowClick={handleRowClick}
					renders={transactionRenderers(t, isDeleteDisabled, batchStatus)}
					rows={items?.items ?? []}
				/>
			)}

			{items?.pagination && items.pagination.count > DEFAULT_PAGE_SIZE && (
				<div className="flex justify-center mt-space-24">
					<Pagination
						currentPage={currentPage}
						dropdownOpenPosition={DropdownPositions.TOP}
						onPageChange={setCurrentPage}
						onPageSizeChange={handlePageSizeChange}
						pageOptions={PAGE_SIZE_OPTIONS}
						pageSize={pageSize}
						pageText={t('pageText')}
						totalCount={items.pagination.count}
						totalCountText={t('totalResults')}
					/>
				</div>
			)}
		</FormProvider>
	);
};
